import { useCallback, useMemo, useRef, useState } from 'react';
import {
  ContextMenu,
  ContextMenuContent,
  ContextMenuTrigger,
} from '../../context-menu';
import { EditorStarterItem } from '../../items/item-type';
import { setSelectedItems } from '../../state/actions/set-selected-items';
import { useWriteContext } from '../../utils/use-context';
import { TimelineItemContextMenu } from './timeline-item-context-menu';
import {
  RemoveSilenceDialog,
  type RemoveSilenceDialogConfirmParams,
} from '../remove-silence-dialog';

export const ItemContextMenuTrigger = ({
  item,
  children,
}: {
  item: EditorStarterItem;
  children: React.ReactNode;
}) => {
  const { setState } = useWriteContext();
  const [removeSilenceDialogOpen, setRemoveSilenceDialogOpen] = useState(false);
  const confirmHandlerRef = useRef<
    ((params: RemoveSilenceDialogConfirmParams) => void | Promise<void>) | null
  >(null);

  const handleContextMenu = useCallback(() => {
    setState({
      update: (state) => {
        if (state.selectedItems.length > 1) {
          return state;
        }
        return setSelectedItems(state, [item.id]);
      },
      commitToUndoStack: true,
    });
  }, [item.id, setState]);

  const handleRequestRemoveSilenceDialog = useCallback(
    (
      onConfirm: (
        params: RemoveSilenceDialogConfirmParams,
      ) => void | Promise<void>,
    ) => {
      confirmHandlerRef.current = onConfirm;
      // ContextMenu onSelect가 flushSync로 동기 처리되므로,
      // ContextMenu의 DismissableLayer가 완전히 정리된 뒤에 Dialog를 열어야
      // 포인터 이벤트 충돌이 발생하지 않는다.
      requestAnimationFrame(() => {
        setRemoveSilenceDialogOpen(true);
      });
    },
    [],
  );

  const handleDialogConfirm = useCallback(
    (params: RemoveSilenceDialogConfirmParams) => {
      confirmHandlerRef.current?.(params);
    },
    [],
  );

  const style = useMemo(() => {
    return {
      display: 'contents',
    };
  }, []);

  return (
    <>
      <ContextMenu>
        <ContextMenuTrigger asChild>
          <div
            onContextMenu={handleContextMenu}
            style={style}
          >
            {children}
          </div>
        </ContextMenuTrigger>
        <ContextMenuContent>
          <TimelineItemContextMenu
            item={item}
            onRequestRemoveSilenceDialog={handleRequestRemoveSilenceDialog}
          />
        </ContextMenuContent>
      </ContextMenu>
      <RemoveSilenceDialog
        open={removeSilenceDialogOpen}
        onOpenChange={setRemoveSilenceDialogOpen}
        onConfirm={handleDialogConfirm}
      />
    </>
  );
};
